import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { Card, CardContent } from '@/components/ui/card';
import { setLanguageFromSession } from '@/i18n';
import { LoginForm } from '../components/LoginForm';

// ATEK Brand Colors
const BRAND = {
  bone: '#F0F0F0',
  cobalt: '#5A1DEF',
  midnight: '#1C143D',
  slate: '#404041',
} as const;

export function LoginPage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading } = useSession();
  const currentLang = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as 'en' | 'fr';

  // Get the redirect URL from query params
  const redirectUrl = searchParams.get('url');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      let destination = '/accounts';

      if (redirectUrl) {
        try {
          const decoded = decodeURIComponent(redirectUrl);
          // Security: Only allow relative paths starting with /
          // Prevent open redirect attacks (e.g., //evil.com or http://evil.com)
          if (decoded.startsWith('/') && !decoded.startsWith('//')) {
            destination = decoded;
          }
        } catch {
          // Malformed URL encoding (e.g., %E0%A4%A), use default destination
        }
      }

      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, isLoading, redirectUrl, navigate]);

  const handleLoginSuccess = () => {
    // The session query will be invalidated by useLogin
    // The useEffect above will handle redirection once isAuthenticated updates
  };

  // Show nothing while checking if already authenticated
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: BRAND.bone }}
      >
        <div className="animate-pulse" style={{ color: BRAND.slate }}>
          {t('loading')}
        </div>
      </div>
    );
  }

  // Already authenticated, will redirect
  if (isAuthenticated) {
    return null;
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 relative"
      style={{ backgroundColor: BRAND.bone }}
    >
      {/* Language toggle */}
      <div className="absolute top-4 right-4 flex items-center gap-1 text-xs" style={{ color: BRAND.slate }}>
        <button
          type="button"
          onClick={() => setLanguageFromSession('en')}
          className={`px-2 py-1 rounded hover:underline ${currentLang === 'en' ? 'font-semibold' : ''}`}
          style={currentLang === 'en' ? { color: BRAND.cobalt } : undefined}
          aria-pressed={currentLang === 'en'}
        >
          EN
        </button>
        <span aria-hidden="true">|</span>
        <button
          type="button"
          onClick={() => setLanguageFromSession('fr')}
          className={`px-2 py-1 rounded hover:underline ${currentLang === 'fr' ? 'font-semibold' : ''}`}
          style={currentLang === 'fr' ? { color: BRAND.cobalt } : undefined}
          aria-pressed={currentLang === 'fr'}
        >
          FR
        </button>
      </div>

      {/* Logo and Tagline */}
      <div className="mb-8 text-center">
        <img
          src="https://storage.googleapis.com/atek-public-assets/brand/logos/atek-logo-main-dark-medium.png"
          alt="ATEK"
          className="h-16 w-auto mx-auto mb-3"
        />
        <p
          className="text-sm tracking-wide"
          style={{ color: BRAND.slate }}
        >
          {t('auth.tagline')}
        </p>
      </div>

      {/* Login Card */}
      <Card className="w-full max-w-sm shadow-lg border-0">
        <CardContent className="pt-8 pb-8 px-8">
          <LoginForm onSuccess={handleLoginSuccess} />
        </CardContent>
      </Card>

      {/* Footer */}
      <p
        className="mt-8 text-xs"
        style={{ color: BRAND.slate }}
      >
        {t('auth.footerTagline')}
      </p>
    </div>
  );
}
